import { useRef } from "react";
import BibleTopBar from "./BibleTopBar";
import BibleVerses from "./BibleVerses";
import BibleBottomBar from "./BibleBottomBar";
import { useScreenSize } from "../hooks/useScreenSize";
import { colors, DEFAULT_COLUMNS, DEFAULT_FONT_SIZE } from "./constants";
import { BooksList, QueriedVerses } from "./BibleSideBar";
import { navigateBible, useFocusedVerse, useUrlQueryParam } from "./routing";

export function mergeHighlight(fullText: string, highlightFragment: string | undefined): string {
  if (!highlightFragment) return fullText;
  const plain = highlightFragment.replace(/<\/?em>/g, '');
  const idx = fullText.indexOf(plain);
  if (idx === -1) return fullText;
  return fullText.slice(0, idx) + highlightFragment + fullText.slice(idx + plain.length);
}

interface BibleContentProps {
  verses: any[];
  chosenTranslation: any;
  chosenBook: any;
  chosenChapter: any;
  books: any[];
  translations: any[];
  queriedVerses: any[];
  queriedBooks: any[];
  queryInput: string;
  setQueryInput: (value: string) => void;
}
export default function BibleContent({
  verses,
  chosenTranslation,
  chosenBook,
  chosenChapter,
  books,
  translations,
  queriedVerses,
  queriedBooks,
  queryInput,
  setQueryInput,
}: BibleContentProps) {
  const [fontSize] = useUrlQueryParam('fontSize', DEFAULT_FONT_SIZE, true);
  const [columns] = useUrlQueryParam('columns', DEFAULT_COLUMNS, true);
  const [focusedVerse, setFocusedVerse] = useFocusedVerse();

  const { screenLargerThan } = useScreenSize()

  const currentLocation = {
    translationSlug: chosenTranslation.slug,
    bookSlug: chosenBook.slug,
    chapterNumber: chosenChapter.chapter_number,
  };

  const chapterQueriedVerses = Array.isArray(queriedVerses)
    ? queriedVerses.filter((v: any) =>
      v[0]?.bible_book_slug === chosenBook.slug &&
      v[0]?.chapter_number == chosenChapter.chapter_number
    )
    : [];

  const highlightMap = Object.fromEntries(
    chapterQueriedVerses.map((v: any) => [v[0].verse_number, mergeHighlight(v[0].text, v[1]?.text)])
  );

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClearQuery = () => {
    navigateBible(currentLocation, { query: '' });
    setQueryInput('');
    searchInputRef.current?.focus();
  };

  return (
    <div style={{
      height: '100%',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      <BibleTopBar
        currentTranslation={chosenTranslation}
        currentBook={chosenBook}
        currentChapter={chosenChapter}
        books={books}
        translations={translations}
        queriedBooks={queriedBooks}
        queriedVerses={queriedVerses}
        chosenBook={chosenBook}
        searchInputRef={searchInputRef}
        queryInput={queryInput}
        setQueryInput={setQueryInput}
        key={`${chosenTranslation.slug}-${chosenBook.slug}-${chosenChapter.chapter_number}`}
      />
      {/* Verses */}
      <div
        key={`bible-verses-${chosenTranslation.slug}-${chosenBook.slug}-${chosenChapter.chapter_number}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: colors['white'],
          flex: 1,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {screenLargerThan('lg') || !queryInput ? (
          <BibleVerses
            verses={verses}
            chosenTranslation={chosenTranslation}
            chosenBook={chosenBook}
            chosenChapter={chosenChapter}
            highlightMap={highlightMap}
            focusedVerse={focusedVerse}
            onFocusVerse={setFocusedVerse}
            columns={columns}
            fontSize={fontSize}
            direction={chosenTranslation.rtl ? 'rtl' : 'ltr'}
          />
        ) : (
          (queriedVerses?.length > 0 || queriedBooks?.length > 0) ? (
            <div
              style={{
                padding: '4px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                overflow: 'auto',
              }}
            >
              <BooksList
                currentTranslation={chosenTranslation}
                currentBook={chosenBook}
                currentChapter={chosenChapter}
                books={queriedBooks}
                chosenBook={chosenBook}
                clearQuery
              />
              <QueriedVerses
                currentTranslation={chosenTranslation}
                currentBook={chosenBook}
                currentChapter={chosenChapter}
                verses={queriedVerses}
                onNavigate={() => setQueryInput('')}
              />
            </div>
          ) : (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              color: colors['black'],
            }}>
              <div>
                No results in this translation ({chosenTranslation.name})
              </div>
              <a
                href="#"
                style={{
                  color: colors['darkPrimary'],
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleClearQuery();
                }}
              >
                Clear query
              </a>
            </div>
          )
        )}
      </div>
      {/* Content bottom bar */}
      {screenLargerThan('lg') ? (
        <BibleBottomBar
          currentTranslation={chosenTranslation}
          currentBook={chosenBook}
          currentChapter={chosenChapter}
        />
      ) : (
        // <MobileBottomNavigationBar
        // />
        <></>
      )}
    </div>
  )
}
